import React from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Marquee — continuous horizontal scroll of a set of items.
 *
 * Direction: right-to-left. Speed: `duration` seconds per full loop.
 * The items list is rendered twice inline so the reset from -50% back to
 * 0% is seamless (no visible gap).
 *
 * Pause on hover: any pointer-over on the track pauses the CSS animation.
 *
 * Reduced-motion fallback: renders a single row with horizontal overflow
 * scroll (native), no animation. Users can still see the full recognition
 * list by dragging / arrow-keying.
 *
 * Not a shadcn wrapper — Phase 3 design-system primitive lives in
 * /components/ui.
 */
export function Marquee({
  items,
  duration = 40,
  className,
  itemClassName,
  ariaLabel = "Marquee",
  testIdBase = "marquee",
}) {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <div
        aria-label={ariaLabel}
        role="region"
        className={cn(
          "overflow-x-auto",
          "no-scrollbar",
          className,
        )}
        data-testid={`${testIdBase}-static`}
      >
        <div className="flex gap-4 whitespace-nowrap py-2">
          {items.map((item, i) => (
            <span
              key={i}
              className={itemClassName}
              data-testid={`${testIdBase}-item-${i}`}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      aria-label={ariaLabel}
      role="region"
      className={cn(
        "group relative overflow-hidden",
        "[--marquee-duration:var(--dur)]",
        className,
      )}
      style={{ "--dur": `${duration}s` }}
      data-testid={testIdBase}
    >
      {/* Edge gradient masks for a soft in/out reveal */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[var(--surface-base)] to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[var(--surface-base)] to-transparent"
      />

      <div className="marquee-track flex w-max gap-4 py-2 will-change-transform group-hover:[animation-play-state:paused]">
        {[0, 1].map((copyIdx) => (
          <div
            key={copyIdx}
            className="flex shrink-0 items-center gap-4"
            aria-hidden={copyIdx === 1 ? "true" : undefined}
          >
            {items.map((item, i) => (
              <span
                key={`${copyIdx}-${i}`}
                className={itemClassName}
                data-testid={
                  copyIdx === 0 ? `${testIdBase}-item-${i}` : undefined
                }
              >
                {item}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Marquee;
